/* eslint-disable max-lines */
import type { Booking } from '@cocoonspace/sdk-js/types/booking'
import type { Event } from '@cocoonspace/sdk-js/types/event'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { AnalyticsInstance } from 'analytics'
import { createContext, type PropsWithChildren, useContext } from 'react'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { AnalyticsConsumer, AnalyticsProvider } from 'use-analytics'

import { ZONES } from '~/config/zones'

namespace Tracking {
	export type ClickTriggerName =
		| 'dashboard-bookASpace-spaceCard'
		| 'dashboard-bookAgain-link'
		| 'dashboard-manageUsers-link'
		| 'dashboard-seeAllCocoon-link'
		| 'dashboard-seeFavorites-link'
		| 'dashboard-seeInvoices-link'
		| 'dashboard-seeMyBookings-link'
		| 'eventCard'
		| 'eventCard-bookSame-button'
		| 'eventCard-cancelPopover-cancelBtn'
		| 'eventCard-cancelPopover-closeBtn'
		| 'eventCard-cancelPopover-confirmBtn'
		| 'eventCard-delete-button'
		| 'extra-dialog-button'
		| 'eventCard-viewInvoice-button'
		| 'favorites-empty-seeCocoons-link'
		| 'favorites-seeCocoons-link'
		| 'favorites-spaceCard'
		| 'featuredEvent-chooseMenu-button'
		| 'featuredEvent-eventCard'
		| 'featuredEvent-share-button'
		| 'landing-meetingRooms-seeAllCocoonBtn'
		| 'landing-hourlyOffices-seeAllCocoonBtn'
		| 'landing-district-seeAllCocoonBtn'
		| 'landing-district-spaceCard'
		| 'microsearch-seeCocoons'
		| 'myEvents-yearFilter-btn'
		| 'myEvents-yearFilter-monthBtn'
		| 'myEvents-yearFilter-nextBtn'
		| 'myEvents-yearFilter-prevBtn'
		| 'navbar-brand-link'
		| 'navbar-findACocoon-button'
		| 'spaceDetails-booking-days-btn'
		| 'spaceDetails-booking-hours-btn'
		| 'userMenu-logout-link'
		| 'userMenu-myAccount-link'
		| 'userMenu-myEvents-link'
		| 'userMenu-myFavorites-link'
		| 'userMenu-dashboard-link'
		| 'userMenu-myInvoices-link'
		| 'breadcrumb-link'

	export interface Dialog {
		dialogName: string
		action: 'open' | 'close'
	}

	export interface Autocomplete {
		city?: string
		postal_code?: string
		query?: string
		header?: string
		latitude?: number
		longitude?: number
	}

	export interface HomeSearch {
		capacity?: string[]
		text?: string
		autocompleteRes?: Autocomplete
	}

	export interface Search {
		filters: any
		resultCount?: number
		zoom: number
		capacities: ReadonlyArray<any>
	}

	export interface EventTracking {
		event: Event
		canal?: string
		platform?: string
	}

	export interface SuggestionsCardClick {
		source: string
		target: string
	}

	export interface UpdateFavorite {
		space: Pick<Space, 'favorite' | 'title'>
		route: string
	}

	export interface SuggestionsSeeMore {
		space: Pick<Space, 'title'>
	}

	export interface NavbarFindCocoonClick {
		zone: string
	}

	export interface CancelEvent {
		route: string
		event: Event
	}

	export interface ShareEvent {
		event: Event
		canal: string
		page: string
	}

	export interface BookingMissingInfos {
		isInfosPersoIncomplete: boolean
		isInfoFactuIncomplete: boolean
		hasNoPaymentMethod: boolean
	}

	export interface BookingValidated {
		event: Event
		booking: Booking
		platform: string
		transaction: string
		canal: string
	}

	export interface BookingPage {
		event: Event
		booking: Booking
	}

	export interface Extension {
		event: Event
		selectedTimeSlot: Pick<Event, 'start' | 'end'>
	}

	export interface PaymentMethodUpdate {
		action: string
		route: string
	}

	export interface ChangeEvent {
		event: Event
		eventOption: Event
		booking?: Booking
		overtime: number
	}

	export interface SearchSpaceCardClick {
		filters: any
		resultCount?: number
		zoom: number
		capacities: ReadonlyArray<any>
		type: string
	}

	export interface AccountPrefZone {
		zone?: string
		source: string
	}

	export interface RecurringOffer {
		source: 'tarifs' | 'sidebar'
		cocoon: string
		type: 'hours' | 'days'
		days?: string[]
		startDate?: string
	}

	export interface RenameEvent {
		source: 'eventDetails' | 'bookingConfirm'
	}

	export interface EventInvitation {
		source: 'eventDetails' | 'bookingConfirm' | 'shareDialog'
		guestCount: number
	}
}

const getSearchTrackingData = ({
	filters,
	resultCount,
	zoom,
	capacities,
}: {
	filters: any
	resultCount?: number
	zoom: number
	capacities: ReadonlyArray<any>
}) => {
	const selectedCapacities = (filters.capacity ?? [])
		.map((capacityValue: string) =>
			capacities.find((cap) => cap.value === capacityValue),
		)
		.filter(Boolean)

	const startMinutes = dateTimeUtils.utils.getTimeAsMinutes(filters.start)
	const endMinutes = dateTimeUtils.utils.getTimeAsMinutes(filters.end)

	return {
		'Address latitude': filters.autocompleteRes?.latitude,
		'Address longitude': filters.autocompleteRes?.longitude,
		'Address type': filters.autocompleteRes?.header,
		'Days before booking': Math.ceil(
			dateTimeUtils(filters.day).diff(dateTimeUtils(), 'days', true),
		),
		Duration:
			filters.start && filters.end ? (endMinutes - startMinutes) / 60 : '',
		'End hour': filters.end,
		'Nb People': selectedCapacities
			.map((cap: { label: string }) => cap.label)
			.join(', '),
		'Nb Results': resultCount || 0,
		'Search Date': filters.day,
		'Selected Address': filters.text,
		'Selected City': filters.autocompleteRes?.city,
		'Selected Zipcode': filters.autocompleteRes?.postal_code,
		'Start hour': filters.start,
		Tag: filters.features?.join(', '),
		'Typed Address': filters.autocompleteRes?.query,
		'Zoom level': zoom,
	}
}

// NOTE: should we use a context to store these functions instead of re-creating them each time we use the hook?
// keep in mind for potential performance issues/high memory usage
const trackingFns = (analytics: AnalyticsInstance) => {
	const _track = analytics.track

	const click = (
		triggerName: Tracking.ClickTriggerName,
		payload: Record<string, any> = {},
	) => _track('Click', { triggerName, ...payload })

	const dialog = (payload: Tracking.Dialog) => _track('Dialog', payload)

	const search = (payload: Tracking.Search) =>
		_track('Search', getSearchTrackingData(payload))

	const personalInfosUpdate = (route: string) =>
		_track('Personal infos update', { route })

	const billingAddressUpdate = (route: string) =>
		_track('Billing address update', { route })

	const event = ({ event, canal, platform }: Tracking.EventTracking) =>
		_track('Event', {
			'Booking ID': event.booking_id,
			Canal: canal,
			Capacity: event.space?.capacity,
			Duration: (event.duration || 0) / 60,
			'End hour': event.end,
			'Event date': event.day,
			Place: event.title,
			Platforme: platform,
			'Start hour': event.start,
			User: event.is_organizer ? 'Organizer' : 'Guest',
		})

	const spaceSuggestionsCardClick = (payload: Tracking.SuggestionsCardClick) =>
		_track('Space suggestions card click', payload)

	const showMapOrList = (value: boolean) =>
		_track(value ? 'Show map' : 'Show list')

	const updateFavorite = ({ space, route }: Tracking.UpdateFavorite) =>
		_track(`Favorite ${!space.favorite ? 'added' : 'deleted'}`, {
			Page: ZONES.some((zone) => route.includes(`/${zone}`))
				? 'Search'
				: 'MyFavorites',
			Place: space.title,
		})

	const spaceSuggestionsSeeMore = ({ space }: Tracking.SuggestionsSeeMore) =>
		_track('Space suggestions see more click', { source: space.title })

	const cancelEvent = (payload: Tracking.CancelEvent) =>
		_track('Cancel event', {
			'Booking date': payload.event.booked_at,
			'Booking ID': payload.event.booking_id,
			Canal: 'Web direct',
			Capacity: payload.event.space?.capacity,
			Duration: payload.event.duration,
			'End hour': payload.event.end,
			'Event date': payload.event.day,
			Page: payload.route === '/events' ? 'My Events' : 'Event',
			Place: payload.event.space?.title,
			'Place price': (payload.event.space_price || 0) / 100,
			Plateforme: 'Cocoon',
			'Service price': (payload.event.extras_price || 0) / 100,
			'Start hour': payload.event.start,
			'Total price': (payload.event.total_price || 0) / 100,
			User: payload.event.is_organizer ? 'Organizer' : 'Guest',
		})

	const seeMaps = (Page: string) => _track('See maps', { Page })
	const calendarShare = (target: string) => _track('Calendar Share', { target })

	const bookingValidated = ({
		event,
		booking,
		platform,
		canal,
		transaction,
	}: Tracking.BookingValidated) => {
		const daysBeforeBooking =
			dateTimeUtils(event.day).diff(dateTimeUtils(), 'day') + 1

		return _track('Booking Validated', {
			'Booking date': booking.created_at,
			'Booking ID': booking.id,
			Canal: canal,
			Capacity: event.space?.capacity,
			'Days before booking': daysBeforeBooking,
			Duration: event.duration,
			'End hour': event.end,
			'Event date': event.day,
			Favorite: event?.space?.favorite ? 'true' : 'false',
			'Hour price': (event.price_hour || 0) / 100,
			nth: event.nth,
			Place: event.space?.title,
			'Place price': (booking.space_price || 0) / 100,
			Plateforme: platform,
			Pricing: event.rebates?.[0]?.code,
			Service: event.extras?.map((extra) => extra.id),
			'Service price': (booking.extras_price || 0) / 100,
			'Start hour': event.start,
			'Total price': (booking.total_price || 0) / 100,
			Transaction: transaction,
			type: event.type,
		})
	}

	const extension = ({ event, selectedTimeSlot }: Tracking.Extension) =>
		_track('Extension', {
			Capacity: event.space?.capacity,
			'Duration extension':
				(dateTimeUtils.utils.getTimeAsMinutes(event.start) -
					dateTimeUtils.utils.getTimeAsMinutes(event.end) -
					(dateTimeUtils.utils.getTimeAsMinutes(
						selectedTimeSlot.start || event.start,
					) -
						dateTimeUtils.utils.getTimeAsMinutes(
							selectedTimeSlot.end || event.end,
						))) /
				60,
			'Duration new': (event.duration || 0) / 60,
			Place: event.space?.title,
		})

	const paymentMethodUpdate = ({
		action,
		route,
	}: Tracking.PaymentMethodUpdate) =>
		_track('Payment method update', { action, route })

	const changeEvent = ({
		event,
		eventOption,
		booking,
		overtime,
	}: Tracking.ChangeEvent) =>
		_track('Change event', {
			'Booking date': booking?.created_at,
			'Booking ID': booking?.id,
			Canal: 'Web direct',
			Capacity: event.space?.capacity,
			Duration: overtime + event.duration! / 60,
			'End hour': eventOption.end,
			'Event date': eventOption.day,
			'Event date old': new Date(event.day as string),
			Place: event.space?.title,
			'Place price': (booking?.space_price as number) / 100,
			Plateforme: 'Cocoon',
			'Price change': -((booking?.balance as number) / 100),
			'Service price': (booking?.extras_price as number) / 100,
			'Start hour': eventOption.start,
			'Total price': booking?.total_price
				? booking?.total_price / 100
				: undefined,
			User: event.is_organizer ? 'Organizer' : 'Guest',
		})

	const updateEmail = () => _track('Email update')

	const updatePassword = () => _track('Password update')

	const recurringOffer = (payload: Tracking.RecurringOffer) =>
		_track('Resa recurrente', payload)

	const eventInvitation = (payload: Tracking.EventInvitation) =>
		_track('eventInvitation', payload)

	const eventExtrasUpdate = () => _track('eventExtrasUpdate')

	const searchAutocompleteSelect = (payload: {
		category: string
		text: string
	}) => _track('searchAutocompleteSelect', payload)

	const track = {
		billingAddressUpdate,
		bookingValidated,
		calendarShare,
		cancelEvent,
		changeEvent,
		click,
		dialog,
		event,
		eventExtrasUpdate,
		eventInvitation,
		extension,
		paymentMethodUpdate,
		personalInfosUpdate,
		recurringOffer,
		search,
		searchAutocompleteSelect,
		seeMaps,
		showMapOrList,
		spaceSuggestionsCardClick,
		spaceSuggestionsSeeMore,
		updateEmail,
		updateFavorite,
		updatePassword,
	}

	return {
		analytics,
		track,
	}
}

export const trackingContext = createContext(
	{} as ReturnType<typeof trackingFns>,
)

interface TrackingProviderProps {
	instance: AnalyticsInstance
}

export const useTracking = () => useContext(trackingContext)

export const TrackingProvider = ({
	instance,
	children,
}: PropsWithChildren<TrackingProviderProps>) => {
	return (
		<AnalyticsProvider instance={instance}>
			<AnalyticsConsumer>
				{(analytics) => (
					<trackingContext.Provider value={trackingFns(analytics)}>
						{children}
					</trackingContext.Provider>
				)}
			</AnalyticsConsumer>
		</AnalyticsProvider>
	)
}
